import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Plus, X, StickyNote, ClipboardList, Globe, Salad } from 'lucide-react';
import { toast } from 'sonner';

const errText = (e, fallback) => {
  const d = e?.response?.data?.detailObject || e?.response?.data?.detail;
  return (typeof d === 'object' ? d?.message : d) || fallback;
};
const CURRENCIES = ['INR', 'USD', 'CAD', 'GBP', 'EUR', 'AUD', 'AED', 'SGD'];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

/** Country + Diet-Chart chips shown on inbox cards and the thread header (never phone / email). */
export const CountryChip = ({ country, dietPlan, compact = false }) => {
  if (!country) return null;
  const intl = country.is_international;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      <Badge variant="outline" data-testid="learner-country-chip"
        className={`text-[10px] gap-0.5 ${intl === false ? 'border-emerald-300 text-emerald-800 bg-emerald-50' : intl ? 'border-sky-300 text-sky-800 bg-sky-50' : 'text-muted-foreground'}`}>
        <Globe className="h-2.5 w-2.5" />{intl === false ? 'India' : intl ? `Outside India${!compact && country.label && country.label !== 'International' ? ` · ${country.label}` : ''}` : 'Country unknown'}
      </Badge>
      {dietPlan?.entitled && (
        <Badge variant="outline" className="text-[10px] gap-0.5 border-amber-300 text-amber-800 bg-amber-50" data-testid="learner-diet-chart-chip">
          <Salad className="h-2.5 w-2.5" />Diet chart{dietPlan.complimentary ? ' · included' : ''}
        </Badge>
      )}
    </span>
  );
};

/** Pointer list editor shared by the Notes dialog and the Close dialog. */
const PointersEditor = ({ pointers, setPointers, summary, setSummary, testPrefix }) => {
  const [draft, setDraft] = useState('');
  const add = () => { const v = draft.trim(); if (!v) return; setPointers(p => [...p, v]); setDraft(''); };
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium">Key pointers</p>
      <ul className="space-y-1">
        {pointers.map((p, i) => (
          <li key={i} className="flex items-start gap-2 text-sm bg-muted/40 rounded-lg px-2 py-1" data-testid={`${testPrefix}-pointer-${i}`}>
            <span className="flex-1">• {p}</span>
            <button type="button" onClick={() => setPointers(ps => ps.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Input value={draft} onChange={e => setDraft(e.target.value)} placeholder="e.g. Advised 3 small meals + iron-rich snacks"
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} data-testid={`${testPrefix}-pointer-input`} />
        <Button type="button" variant="outline" onClick={add} data-testid={`${testPrefix}-pointer-add`}><Plus className="h-4 w-4" /></Button>
      </div>
      <p className="text-xs font-medium pt-1">Summary</p>
      <Textarea value={summary} onChange={e => setSummary(e.target.value)} rows={3} placeholder="Short clinical summary of this conversation (optional)" data-testid={`${testPrefix}-summary`} />
    </div>
  );
};

const NoteRow = ({ n }) => (
  <div className="rounded-lg border p-2.5 text-sm" data-testid="provider-note-row">
    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
      <span>{n.provider_name || 'Nutritionist'} · episode {n.episode}{n.source === 'close' ? ' · on close' : ''}</span>
      <span>{fmtDate(n.created_at)}</span>
    </div>
    {(n.pointers || []).length > 0 && <ul className="mt-1 space-y-0.5">{n.pointers.map((p, i) => <li key={i}>• {p}</li>)}</ul>}
    {n.summary && <p className="mt-1 text-foreground/90 whitespace-pre-wrap">{n.summary}</p>}
  </div>
);

/** Private clinical notes — read by medical staff + superadmin; the assigned nutritionist can add. */
export const NotesDialog = ({ consultId, open, onOpenChange, canAdd = true }) => {
  const [notes, setNotes] = useState(null);
  const [pointers, setPointers] = useState([]);
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const load = () => api.get(`/api/medical/consult/${consultId}/notes`).then(r => setNotes(r.data.notes || [])).catch(() => setNotes([]));
  useEffect(() => { if (open) { setNotes(null); setAdding(false); setPointers([]); setSummary(''); load(); } }, [open, consultId]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy(true);
    try { await api.post(`/api/medical/consult/${consultId}/notes`, { pointers, summary }); toast.success('Note saved'); setPointers([]); setSummary(''); setAdding(false); load(); }
    catch (e) { toast.error(errText(e, 'Could not save the note')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="provider-notes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><StickyNote className="h-4 w-4 text-amber-600" />Internal notes</DialogTitle>
          <DialogDescription>Visible to the medical team and superadmin only. The learner never sees these. Earlier notes are never edited.</DialogDescription>
        </DialogHeader>
        <div className="max-h-60 overflow-y-auto space-y-2">
          {notes === null && <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin" /></div>}
          {notes && notes.length === 0 && <p className="text-sm text-muted-foreground text-center py-3" data-testid="provider-notes-empty">No notes yet for this learner.</p>}
          {notes && notes.map(n => <NoteRow key={n.id} n={n} />)}
        </div>
        {canAdd && (adding ? (
          <div className="border-t pt-3"><PointersEditor pointers={pointers} setPointers={setPointers} summary={summary} setSummary={setSummary} testPrefix="note" /></div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setAdding(true)} data-testid="provider-notes-add-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add a note</Button>
        ))}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          {canAdd && adding && <Button onClick={save} disabled={busy || (!pointers.length && !summary.trim())} className="bg-emerald-600 hover:bg-emerald-700" data-testid="provider-notes-save-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save note'}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Close the chat; optionally append a private note for this episode in the same step. */
export const CloseDialog = ({ consultId, open, onOpenChange, onClosed }) => {
  const [pointers, setPointers] = useState([]);
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPointers([]); setSummary(''); } }, [open]);
  const close = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/api/medical/consult/${consultId}/close`, { pointers, summary });
      toast.success(r.data?.note_id ? 'Conversation closed · note saved' : 'Conversation closed');
      onOpenChange(false); onClosed?.(r.data);
    } catch (e) { toast.error(errText(e, 'Could not close')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="close-consult-dialog">
        <DialogHeader>
          <DialogTitle>Close this conversation</DialogTitle>
          <DialogDescription>Add your summary for the medical record (private — the learner never sees it). She can reopen the same thread anytime; the full history stays.</DialogDescription>
        </DialogHeader>
        <PointersEditor pointers={pointers} setPointers={setPointers} summary={summary} setSummary={setSummary} testPrefix="close" />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={close} disabled={busy} data-testid="close-consult-confirm-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (pointers.length || summary.trim() ? 'Save note & close' : 'Close without note')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Dietician raises a payment: currency + amount, that's it. Razorpay link is created on the learner's tap. */
export const PaymentRequestDialog = ({ consultId, open, onOpenChange, suggestedCurrency = 'INR', onCreated }) => {
  const [currency, setCurrency] = useState(suggestedCurrency);
  const [amount, setAmount] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setCurrency(suggestedCurrency || 'INR'); setAmount(''); setLabel(''); } }, [open, suggestedCurrency]);
  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter an amount'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/api/medical/consult/${consultId}/payment-request`, { amount: amt, currency, label: label.trim() || 'Diet Plan' });
      toast.success(`Payment request for ${r.data.request.amount_str} sent`);
      onOpenChange(false); onCreated?.(r.data);
    } catch (e) { toast.error(errText(e, 'Could not create the payment request')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="payment-request-dialog">
        <DialogHeader>
          <DialogTitle>Request a payment</DialogTitle>
          <DialogDescription>A Razorpay link is created for the learner inside the chat (and by email). Recorded as a Diet Payment and shared with OPS once paid.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-[110px_1fr] gap-2">
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger data-testid="payment-request-currency"><SelectValue /></SelectTrigger>
            <SelectContent position="popper" className="z-[200]">{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" min="1" step="0.01" placeholder="Amount" value={amount} onChange={e => setAmount(e.target.value)} data-testid="payment-request-amount" autoFocus />
        </div>
        <Input placeholder="What is it for? (optional — defaults to Diet Plan)" value={label} onChange={e => setLabel(e.target.value)} data-testid="payment-request-label" />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !amount} className="bg-emerald-600 hover:bg-emerald-700" data-testid="payment-request-submit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send payment request'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Schedule a Zoom video consultation from inside the chat. */
export const ScheduleCallDialog = ({ consultId, open, onOpenChange, onScheduled }) => {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState('30');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  useEffect(() => { if (open) { setDate(''); setTime(''); setDuration('30'); setNote(''); } }, [open]);
  const submit = async () => {
    if (!date || !time) { toast.error('Pick a date and time'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/api/medical/consult/${consultId}/video-call`, { date, time, timezone: tz, duration_minutes: parseInt(duration, 10), note: note.trim() });
      toast.success('Video consultation scheduled — the learner has been notified');
      onOpenChange(false); onScheduled?.(r.data);
    } catch (e) { toast.error(errText(e, 'Could not schedule the call')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="schedule-call-dialog">
        <DialogHeader>
          <DialogTitle>Schedule a video consultation</DialogTitle>
          <DialogDescription>A Zoom meeting is created; both of you get an email and an app notification, plus a reminder 30 minutes before. Times are in your timezone ({tz}).</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="schedule-call-date" />
          <Input type="time" value={time} onChange={e => setTime(e.target.value)} data-testid="schedule-call-time" />
        </div>
        <Select value={duration} onValueChange={setDuration}>
          <SelectTrigger data-testid="schedule-call-duration"><SelectValue /></SelectTrigger>
          <SelectContent position="popper" className="z-[200]">{['15', '20', '30', '45', '60'].map(d => <SelectItem key={d} value={d}>{d} minutes</SelectItem>)}</SelectContent>
        </Select>
        <Textarea rows={2} placeholder="Note for the learner (optional)" value={note} onChange={e => setNote(e.target.value)} data-testid="schedule-call-note" />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !date || !time} className="bg-sky-600 hover:bg-sky-700" data-testid="schedule-call-submit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Schedule'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ k, v }) => (v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)) ? null : (
  <div className="flex gap-2 text-sm"><span className="text-muted-foreground w-36 shrink-0">{k}</span><span className="flex-1">{Array.isArray(v) ? v.join(', ') : String(v)}</span></div>
);

/** Medical history the dietician may see — pregnancy week, coach onboarding summary, conditions, appointments. No contact details. */
export const LearnerBriefDialog = ({ consultId, open, onOpenChange }) => {
  const [brief, setBrief] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!open) return;
    setBrief(null); setErr(null);
    api.get(`/api/medical/consult/${consultId}/learner-brief`).then(r => setBrief(r.data)).catch(e => setErr(errText(e, 'Could not load')));
  }, [open, consultId]);
  const c = brief?.coach || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="learner-brief-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardList className="h-4 w-4 text-emerald-600" />{brief ? `${brief.first_name} — medical brief` : 'Medical brief'}</DialogTitle>
          <DialogDescription>What she has shared with SoulAI. Documents appear only when she shares them in this chat.</DialogDescription>
        </DialogHeader>
        {!brief && !err && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>}
        {err && <p className="text-sm text-rose-600" data-testid="learner-brief-error">{err}</p>}
        {brief && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-1.5">
              <CountryChip country={brief.country} dietPlan={brief.diet_plan} />
              {brief.tier && <Badge variant="outline" className="text-[10px] capitalize">{brief.tier}</Badge>}
              {brief.risk_level && <Badge variant="outline" className={`text-[10px] ${brief.risk_level === 'high' ? 'border-rose-300 text-rose-700' : 'border-emerald-300 text-emerald-700'}`}>{brief.risk_level} risk</Badge>}
            </div>
            <section className="space-y-1" data-testid="learner-brief-pregnancy">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pregnancy</p>
              <Row k="Stage" v={brief.stage} />
              {brief.stage === 'postnatal' ? <Row k="Weeks postpartum" v={brief.weeks_postpartum} /> : <>
                <Row k="Week" v={brief.week ? `${brief.week} (trimester ${brief.trimester})` : null} />
                <Row k="EDD" v={brief.edd} />
                <Row k="Days to EDD" v={brief.days_to_edd} />
              </>}
              <Row k="Latest weight" v={brief.latest_weight ? `${brief.latest_weight.kg} kg` : null} />
              <Row k="Conditions" v={brief.profile_conditions} />
            </section>
            <section className="space-y-1" data-testid="learner-brief-coach">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">SoulAI Coach onboarding</p>
              {!c.onboarded ? <p className="text-sm text-muted-foreground">Not completed yet.</p> : <>
                <Row k="Plan mode" v={c.plan_mode} />
                <Row k="Conditions" v={c.conditions} />
                <Row k="Avoid" v={c.avoid_if} />
                <Row k="IVF / IUI" v={c.ivf_iui ? 'Yes' : null} />
                <Row k="Fitness level" v={c.fitness_level} />
                <Row k="Goals" v={c.goals} />
                <Row k="Workout before" v={c.workout_before} />
                <Row k="Workout during" v={c.workout_during} />
                <Row k="Lifestyle before" v={c.lifestyle_before} />
                <Row k="Lifestyle during" v={c.lifestyle_during} />
                <Row k="Last scan" v={c.last_scan_note} />
                <Row k="Doctor clearance" v={c.doctor_clearance} />
                <Row k="Delivery" v={c.delivery_type ? `${c.delivery_type}${c.delivery_date ? ` · ${c.delivery_date}` : ''}` : null} />
                <Row k="Postnatal conditions" v={c.postnatal_conditions} />
                <Row k="Classes / week" v={c.classes_per_week ? `${c.classes_per_week} (${c.practised_this_week ?? 0} done this week)` : null} />
                <Row k="Recent focus" v={c.recent_focus} />
              </>}
            </section>
            {(brief.appointments || []).length > 0 && (
              <section className="space-y-1" data-testid="learner-brief-appointments">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Appointments & outcomes</p>
                {brief.appointments.map((a, i) => (
                  <div key={i} className="text-sm rounded-lg border px-2.5 py-1.5">
                    <div className="flex justify-between"><span className="font-medium capitalize">{a.title || a.type?.replace('_', ' ')}</span><span className="text-xs text-muted-foreground">{a.date || (a.tentative_week ? `~wk ${a.tentative_week}` : '')} · {a.status}</span></div>
                    {a.outcome && <p className="text-xs text-muted-foreground mt-0.5">
                      {a.outcome.reports_normal === true ? 'Reports normal' : a.outcome.reports_normal === false ? 'Something came up' : ''}
                      {a.outcome.conditions_added?.length ? ` · added: ${a.outcome.conditions_added.join(', ')}` : ''}
                      {a.outcome.conditions_removed?.length ? ` · cleared: ${a.outcome.conditions_removed.join(', ')}` : ''}
                    </p>}
                  </div>
                ))}
              </section>
            )}
            <p className="text-[11px] text-muted-foreground">{brief.documents_note} {brief.shared_doc_count ? `${brief.shared_doc_count} shared in this chat.` : ''}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
